import "./index.css";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import moment from "moment";
import { FaClock } from "react-icons/fa6";
import { Dialog, DialogTrigger } from "components/Dialog";
import MeetingDialog from "components/MeetingDialog";
import {
  MeetingAdminResponse,
  AccountingSystemForCalculatingFinancialStatistics,
  ApprovalStatus,
  TadpoleToTutorTransactionType,
  Auth0AccountRole,
  TransactionStatus,
  TadpoleToTutorTransactionResponse,
  TutorLimitedResponse,
  PaymentsService,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  getTransactionStatusColor,
  isFailed,
  isPaymentSuccessful,
} from "pages/manage/students/[id]/PaymentsTab/Transactions/statusHelper";
import RetryModal from "pages/manage/students/[id]/PaymentsTab/Modals/Actions/Retry";
import TransactionDetailsPopup from "pages/manage/students/[id]/PaymentsTab/Transactions/TransactionMeetingDetails";
import { useState } from "react";

/** Human-readable transaction types. */
interface TransactionTypeMapping {
  value: TadpoleToTutorTransactionType;
  label: string | ((txn: TadpoleToTutorTransactionResponse) => string);
}

export const tadpoleToTutorTransactionTypeToHumanReadable: TransactionTypeMapping[] =
  [
    {
      value:
        TadpoleToTutorTransactionType.AUTOMATIC_PAYMENT_FOR_A_GROUP_OF_MEETINGS,
      label: "Automatic Payroll Payment",
    },
    {
      value:
        TadpoleToTutorTransactionType.EXTERNAL_PAYMENT_FOR_A_GROUP_OF_MEETINGS,
      label: "Payment on External Platform",
    },
    {
      value: TadpoleToTutorTransactionType.MEMBER_OF_PAYMENT_GROUP,
      label: (txn: TadpoleToTutorTransactionResponse) =>
        txn.meeting_tutor_id
          ? "Pending Automatic Payment, queued to be included in next payroll run"
          : "Pending Direct Payment, queued to be included in next payroll run",
    },
  ];

/**
 * Determine whether we show "Refund" or "Retry" as the status action.
 */
const getStatusAction = (status: TransactionStatus) => {
  if (isPaymentSuccessful(status)) {
    return { value: "SUCCESSFUL", label: "Refund" };
  }
  if (isFailed(status)) {
    return { value: "FAILED", label: "Retry" };
  }
  return null;
};

function Transactions({
  transactions,
  tutor,
  accountingSystem,
}: {
  transactions: TadpoleToTutorTransactionResponse[] | MeetingAdminResponse[];
  tutor: TutorLimitedResponse;
  accountingSystem: AccountingSystemForCalculatingFinancialStatistics;
}) {
  const isMeetingSystem =
    accountingSystem ===
    AccountingSystemForCalculatingFinancialStatistics.MEETINGS;

  const isTransactions =
    accountingSystem ===
    AccountingSystemForCalculatingFinancialStatistics.TRANSACTIONS;

  const [updatingStatusIds, setUpdatingStatusIds] = useState<{
    [key: string]: boolean;
  }>({});

  const updateStatusOfTutorPaymentOnExternalPlatform = async (
    transaction: TadpoleToTutorTransactionResponse
  ) => {
    if (
      transaction.tadpole_to_tutor_transaction_type !==
      TadpoleToTutorTransactionType.EXTERNAL_PAYMENT_FOR_A_GROUP_OF_MEETINGS
    ) {
      return;
    }

    setUpdatingStatusIds((prev) => ({
      ...prev,
      [transaction.id]: true,
    }));

    try {
      const newStatus =
        transaction.status ===
        TransactionStatus.TUTOR_PAYMENT_PENDING_ON_EXTERNAL_PAYROLL_PLATFORM
          ? TransactionStatus.TUTOR_PAYMENT_SUCCESSFUL_ON_EXTERNAL_PAYROLL_PLATFORM
          : TransactionStatus.TUTOR_PAYMENT_PENDING_ON_EXTERNAL_PAYROLL_PLATFORM;

      await PaymentsService.updateStatusOfTutorPaymentOnExternalPayrollPlatform(
        {
          transactionId: transaction.id,
          status: newStatus,
        }
      );

      // Update the transaction status in the UI
      transaction.status = newStatus;
    } catch (error) {
      console.error("Failed to update transaction status:", error);
    } finally {
      setUpdatingStatusIds((prev) => ({
        ...prev,
        [transaction.id]: false,
      }));
    }
  };

  return (
    <div className="w-full max-h-[50rem] overflow-y-auto sm:overflow-x-visible max-sm:overflow-x-auto">
      <table className="w-full border-collapse">
        {/* Sticky header row */}
        <thead className="sticky top-0 z-3 shadow-sm bg-white">
          {isTransactions && (
            <tr className="border-b-2 border-gray-300">
              <th className="text-left p-2 text-sm font-bold w-[15%]">Date</th>
              <th className="text-left p-2 text-sm font-bold w-[15%]">
                Amount
              </th>
              <th className="text-left p-2 text-sm font-bold w-[15%]">
                Service
              </th>
              <th className="text-left p-2 text-sm font-bold w-[15%]">Type</th>
              <th className="p-2 text-sm font-bold w-[15%] text-center">
                Status
              </th>
              <th className="p-2 text-sm font-bold w-[15%] text-center">
                Actions
              </th>
            </tr>
          )}
          {isMeetingSystem && (
            <tr className="border-b-2 border-gray-300">
              <th className="text-left p-2 text-sm font-bold w-[8.5%]">Date</th>
              <th className="text-left p-2 text-sm font-bold w-[18%]">
                Service
              </th>
              <th className="text-left p-2 text-sm font-bold w-[19%]">
                Revenue
              </th>
              <th className="text-left p-2 text-sm font-bold w-[20%]">
                Compensation
              </th>
              <th className="text-left p-2 text-sm font-bold w-[10%]">
                Net Income
              </th>
              <th className="p-2 text-sm font-bold w-[10%] text-center">
                Status
              </th>
              <th className="p-2 text-sm font-bold w-[10%] text-center">
                Actions
              </th>
            </tr>
          )}
        </thead>

        <tbody>
          {transactions.map((transaction, index) => {
            if (isTransactions) {
              const txn = transaction as TadpoleToTutorTransactionResponse;
              const date = moment(txn.timestamp).format("MM/DD/YYYY");
              const amount = (txn.amount / 100).toFixed(2);
              const service = txn.reason;
              const type =
                tadpoleToTutorTransactionTypeToHumanReadable.find(
                  (t) => txn.tadpole_to_tutor_transaction_type === t.value
                )?.label ?? "Unknown";
              const typeLabel = typeof type === "function" ? type(txn) : type;

              const statusIcon = (() => {
                const statusColor = getTransactionStatusColor(txn.status);
                const isExternalPayment =
                  txn.tadpole_to_tutor_transaction_type ===
                  TadpoleToTutorTransactionType.EXTERNAL_PAYMENT_FOR_A_GROUP_OF_MEETINGS;
                const isUpdating = updatingStatusIds[txn.id];

                const icon = (() => {
                  switch (statusColor) {
                    case "green":
                      return (
                        <IoCheckmarkCircle className="mx-auto green-mark" />
                      );
                    case "purple":
                      return (
                        <IoCheckmarkCircle className="mx-auto purple-mark" />
                      );
                    case "orange":
                      return (
                        <FaClock
                          className="mx-auto"
                          style={{ color: "#FFB341", fontSize: "20px" }}
                        />
                      );
                    case "red":
                      return <IoCloseCircle className="mx-auto red-mark" />;
                    default:
                      return (
                        <FaClock
                          className="mx-auto"
                          style={{ color: "lightblue", fontSize: "20px" }}
                        />
                      );
                  }
                })();

                if (isExternalPayment) {
                  return (
                    <button
                      onClick={() =>
                        updateStatusOfTutorPaymentOnExternalPlatform(txn)
                      }
                      disabled={isUpdating}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      {icon}
                    </button>
                  );
                }

                return icon;
              })();

              const statusAction = getStatusAction(txn.status);

              const actions = (
                <div className="flex flex-row gap-1 items-center justify-center">
                  {([
                    TadpoleToTutorTransactionType.AUTOMATIC_PAYMENT_FOR_A_GROUP_OF_MEETINGS,
                    TadpoleToTutorTransactionType.EXTERNAL_PAYMENT_FOR_A_GROUP_OF_MEETINGS,
                  ].includes(txn.tadpole_to_tutor_transaction_type) ||
                    (txn.tadpole_to_tutor_transaction_type ===
                      TadpoleToTutorTransactionType.MEMBER_OF_PAYMENT_GROUP &&
                      txn.meeting_tutor_id)) && (
                    <LazyTransactionDetailsPopup transaction={txn} />
                  )}

                  {statusAction?.label === "Retry" && !txn.retried && (
                    <Dialog>
                      <DialogTrigger>
                        <Button
                          color={ButtonColor.SKYBLUE}
                          size={ButtonSize.EXTRA_SMALL}
                          fill={ButtonFill.HOLLOW}
                        >
                          {statusAction.label}
                        </Button>
                      </DialogTrigger>
                      {statusAction.label === "Retry" && (
                        <RetryModal transaction={txn} />
                      )}
                    </Dialog>
                  )}
                </div>
              );

              return (
                <tr
                  key={index}
                  className="bg-white border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-sm">{date}</td>
                  <td className="p-2 text-sm">${amount}</td>
                  <td className="p-2 text-sm">{service}</td>
                  <td className="p-2 text-sm">{typeLabel}</td>
                  <td className="p-2 text-sm text-center">{statusIcon}</td>
                  <td className="p-2 text-sm text-center">{actions}</td>
                </tr>
              );
            }

            if (isMeetingSystem) {
              const meeting = transaction as MeetingAdminResponse;
              const date = moment(meeting.start).format("MM/DD/YYYY");
              const service = meeting.name;

              const totalBilled = meeting.students.reduce(
                (sum, mt) =>
                  sum +
                  (mt.rate_student_billed_at / 100) * (meeting.duration / 60),
                0
              );
              const revenue =
                meeting.tutors.length > 0
                  ? totalBilled / meeting.tutors.length
                  : 0;
              const compensation = meeting.tutors
                .filter((mt) => mt.tutor.id === tutor.id)
                .reduce(
                  (sum, mt) =>
                    sum +
                    (mt.rate_tutor_paid_at / 100) * (meeting.duration / 60),
                  0
                );
              const netIncome = revenue - compensation;

              const statusIcon =
                meeting.reviewed_and_approved_by_admin ===
                ApprovalStatus.APPROVED ? (
                  <IoCheckmarkCircle className="mx-auto green-mark" />
                ) : meeting.reviewed_and_approved_by_admin ===
                  ApprovalStatus.DENIED ? (
                  <IoCloseCircle className="mx-auto red-mark" />
                ) : (
                  <FaClock
                    className="mx-auto"
                    style={{ color: "#FFB341", fontSize: "20px" }}
                  />
                );

              const actions = (
                <div className="flex flex-row gap-1 items-center justify-center">
                  <LazyMeetingDialog meeting={meeting} />
                </div>
              );

              return (
                <tr
                  key={index}
                  className="bg-white border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-sm">{date}</td>
                  <td className="p-2 text-sm">{service}</td>
                  <td className="p-2 text-sm">${revenue.toFixed(2)}</td>
                  <td className="p-2 text-sm">${compensation.toFixed(2)}</td>
                  <td className="p-2 text-sm">${netIncome.toFixed(2)}</td>
                  <td className="p-2 text-sm text-center">{statusIcon}</td>
                  <td className="p-2 text-sm text-center">{actions}</td>
                </tr>
              );
            }

            return null;
          })}
        </tbody>
      </table>
    </div>
  );
}

function LazyTransactionDetailsPopup({
  transaction,
}: {
  transaction: TadpoleToTutorTransactionResponse;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.PURPLE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          View
        </Button>
      </DialogTrigger>
      {open && <TransactionDetailsPopup transaction={transaction} />}
    </Dialog>
  );
}

function LazyMeetingDialog({ meeting }: { meeting: MeetingAdminResponse }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.PURPLE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          View
        </Button>
      </DialogTrigger>
      {open && (
        <MeetingDialog
          isCreate={false}
          role={Auth0AccountRole.ORG_ADMIN}
          event={meeting}
          setEvents={() => Promise.resolve()}
        />
      )}
    </Dialog>
  );
}

export default Transactions;
